/*
 * EwClickIds: keeps the visitor's latest Google Ads click, so the cart can put it on the
 * order as `_ew_*` attributes and the order can be uploaded to Google Ads as an offline
 * conversion. Shopify stores an order's landing page without its query string, so the click
 * ID is gone unless the browser keeps it.
 *
 * - On every page load it reads `gclid`, `gbraid`, `wbraid` and `gad_campaignid` from the
 *   address, and nothing else. With at least one of the three click IDs the whole stored
 *   record is replaced: the last click wins and two clicks never mix. The same click seen
 *   again (a reload, the Back button) keeps its first time, so the cart is not rewritten.
 * - The record lives in localStorage `ew:click-ids` for 90 days, Google's longest conversion
 *   window: {gclid, gbraid, wbraid, gad_campaignid, click_at (ISO 8601, UTC), landing_path}.
 *   Safari deletes script-written storage after 7 days without a visit.
 * - Consent: a click ID is an advertising identifier, so it is stored only while Shopify's
 *   Customer Privacy API says marketing is allowed. Where no consent is required that is
 *   from the first page; behind the cookie banner (EEA, UK) the click waits in memory and is
 *   stored if the visitor accepts on that page. An explicit refusal deletes the record, and
 *   attributes() then answers blanks, so the cart takes the click off again.
 * - It never writes to the cart. The cart page does (Cart.syncTracking in assets/cart.js),
 *   in its own write queue, from attributes() and differs().
 *
 *   EwClickIds.record()                    → the stored click, or null
 *   EwClickIds.attributes()                → the five cart attributes | blanks | null
 *   EwClickIds.differs(cartAttrs, wanted)  → true when the cart does not hold `wanted`
 *
 * Loaded on every page (layout/theme.liquid) and on the quiz page, which has no layout
 * (snippets/gift-finder.liquid). Never throws.
 */
(function () {
  const STORAGE_KEY = 'ew:click-ids';
  const TTL_MS = 90 * 24 * 60 * 60 * 1000;
  const CLICK_IDS = ['gclid', 'gbraid', 'wbraid'];
  const CAMPAIGN = 'gad_campaignid';
  const TOKEN = /^[A-Za-z0-9_-]{1,200}$/;
  const DIGITS = /^\d{1,20}$/;
  // Every cart write carries all five, blank where the click had none, so a gbraid click
  // never sits beside an older gclid.
  const ATTRIBUTES = {
    _ew_gclid: 'gclid',
    _ew_gbraid: 'gbraid',
    _ew_wbraid: 'wbraid',
    _ew_gad_campaignid: CAMPAIGN,
    _ew_click_at: 'click_at',
  };

  // The address and storage are both in the visitor's hands: only plain tokens may reach
  // the order, the admin and the analyst's export.
  function clean(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const record = {};
    let found = false;
    CLICK_IDS.forEach(function (name) {
      const value = typeof raw[name] === 'string' ? raw[name] : '';
      record[name] = TOKEN.test(value) ? value : '';
      if (record[name]) found = true;
    });
    const at = Date.parse(raw.click_at);
    if (!found || isNaN(at)) return null;
    const campaign = typeof raw[CAMPAIGN] === 'string' ? raw[CAMPAIGN] : '';
    record[CAMPAIGN] = DIGITS.test(campaign) ? campaign : '';
    record.click_at = new Date(at).toISOString();
    record.landing_path = String(raw.landing_path || '').slice(0, 200);
    return record;
  }

  function parse(search, path, now) {
    let params;
    try {
      params = new URLSearchParams(search || '');
    } catch (error) {
      return null;
    }
    const raw = { click_at: new Date(now).toISOString(), landing_path: path };
    CLICK_IDS.concat(CAMPAIGN).forEach(function (name) {
      raw[name] = params.get(name) || '';
    });
    return clean(raw);
  }

  function expired(record, now) {
    return now - Date.parse(record.click_at) > TTL_MS;
  }

  function sameClick(a, b) {
    if (!a || !b) return false;
    return CLICK_IDS.concat(CAMPAIGN).every(function (name) {
      return a[name] === b[name];
    });
  }

  function toAttributes(record) {
    const attributes = {};
    Object.keys(ATTRIBUTES).forEach(function (key) {
      attributes[key] = record ? record[ATTRIBUTES[key]] : '';
    });
    return attributes;
  }

  function differs(cartAttributes, wanted) {
    const held = cartAttributes || {};
    return Object.keys(wanted).some(function (key) {
      return String(held[key] == null ? '' : held[key]) !== wanted[key];
    });
  }

  function forget() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Storage is blocked, so nothing was kept either.
    }
  }

  function read(now) {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const record = clean(JSON.parse(raw));
      if (record && !expired(record, now)) return record;
    } catch (error) {
      // Unreadable counts as expired.
    }
    forget();
    return null;
  }

  function save(record) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (error) {
      // Storage blocked or full: losing this click is accepted.
    }
  }

  // Only an explicit "no" is a refusal. Shopify's own record can be missing for a moment (a
  // cleared cookie, until the banner re-sends the stored choice), and that must neither
  // delete a click the visitor agreed to nor store one they have not agreed to yet.
  function consent() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.marketingAllowed !== 'function') {
        return 'unknown';
      }
      if (privacy.marketingAllowed()) return 'yes';
      const given =
        typeof privacy.currentVisitorConsent === 'function'
          ? privacy.currentVisitorConsent()
          : null;
      return given && given.marketing === 'no' ? 'no' : 'unknown';
    } catch (error) {
      return 'unknown';
    }
  }

  // Fails closed: when the consent API cannot be loaded `use` never runs, and nothing is
  // stored.
  function withPrivacy(use) {
    try {
      const shopify = window.Shopify;
      if (shopify && shopify.customerPrivacy) {
        use();
        return;
      }
      if (!shopify || typeof shopify.loadFeatures !== 'function') return;
      shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) {
          if (!error) use();
        },
      );
    } catch (error) {
      // Same as an API that did not load.
    }
  }

  let pending = null;

  // An undecided click stays in `pending`: the banner may still be answered on this page.
  function settle() {
    try {
      const state = consent();
      if (state === 'no') forget();
      if (state !== 'yes' || !pending) return;
      if (!sameClick(read(Date.now()), pending)) save(pending);
      pending = null;
    } catch (error) {
      // Tracking must not surface an error on the page.
    }
  }

  function record() {
    return consent() === 'no' ? null : read(Date.now());
  }

  // null, not blanks, without a record: a cart outlives storage that Safari cleared, and
  // keeps the click it already holds.
  function attributes() {
    if (consent() === 'no') return toAttributes(null);
    const current = read(Date.now());
    return current ? toAttributes(current) : null;
  }

  window.EwClickIds = {
    record: record,
    attributes: attributes,
    differs: differs,
    _test: {
      STORAGE_KEY: STORAGE_KEY,
      TTL_MS: TTL_MS,
      parse: parse,
      clean: clean,
      expired: expired,
      sameClick: sameClick,
      toAttributes: toAttributes,
    },
  };

  try {
    pending = parse(
      window.location.search,
      window.location.pathname,
      Date.now(),
    );
    // The consent API is loaded only for a visitor with a click, new or stored.
    if (pending || window.localStorage.getItem(STORAGE_KEY)) {
      withPrivacy(settle);
      document.addEventListener('visitorConsentCollected', settle);
    }
  } catch (error) {
    // No address or no storage: there is nothing to keep.
  }
})();
